import { FormikContextType } from "formik";
import React from "react";
import Label from "../../components/label/label";
import PaginatedSearchableSelectField from "../../components/selection-field/paginated-searchable-select-field";
import SettingCheckboxListField from "../../components/settingFields/SettingCheckboxListField";
import SettingDateField from "../../components/settingFields/SettingDateField";
import SettingSelectField, { SelectType } from "../../components/settingFields/SettingSelectField";
import { SettingSummaryField } from "../../components/settingFields/SettingSummaryField";
import { SettingTextField } from "../../components/settingFields/SettingTextField";
import { SettingToggleField } from "../../components/settingFields/SettingToggleField";
import TableTab from "../../components/tabs/table-tab";
import fieldUiType from "../../constants/field-ui-type";
import settingTypesBasicFields from "../../constants/setting-types-basic-fields";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Translator } from "../../contexts/translator";
import DeviceTimePatternColumns from "../../models/ColumnDefinitions/DeviceTimePatternColumns";
import ReaderColumns from "../../models/ColumnDefinitions/ReaderColumns";
import { DeviceImprox } from "../../models/DeviceImprox";
import { DeviceTimePattern } from "../../models/DeviceTimePattern";
import { ListQuery } from "../../models/ListQuery";
import DeviceTimePatternSearch from "../../models/search/DeviceTimePatternSearch";
import ReaderSearch from "../../models/search/ReaderSearch";
import { removeColumns } from "../../models/utilities/columnHelper";
import { getAccessGroupsNamesWithPagination } from "../../services/accessGroupService";
import { getCompanyNamesWithPagination } from "../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../services/departmentService";
import { getDeviceImprox } from "../../services/deviceImproxService";
import { getDeviceTimePatternNames, getDeviceTimePatterns } from "../../services/deviceTimePatternService";
import { getMessageNamesWithPagination } from "../../services/messageService";
import { getPrintTemplatesWithPagination } from "../../services/printTemplateService";
import { getReaderNamesWithPagination } from "../../services/readerService";
import { getZoneNamesWithPagination } from "../../services/zoneService";
import { appendFilter } from "../../utilities/filters";
import {
    isPhoneNumberPossibleValid,
    isPhoneNumberValid,
    phoneNumberFieldRestriction,
    requiredValidation,
    restrictToNumbersOnly,
    validateEmail,
    validateIntegerWithinBounds,
    validateItemName, validateZeroAllowed
} from "../../utilities/Validation";
import { DEVICE_CATEGORIES } from "../../constants/device-constants";
import { DeviceImproxColumns } from "../../models/ColumnDefinitions/DeviceImproxColumns";
import DeviceImproxSearch from "../../models/search/DeviceImproxSearch";

export class CustomFieldBuilder {
    private formik: FormikContextType<any>;
    private storedLanguage = window.sessionStorage.getItem("selectedLanguage");
    private translate: Translator = new Translator(this.storedLanguage || 'en');

    constructor(formik: FormikContextType<any>, translate: Translator) {
        this.formik = formik;
        this.translate = translate;
    }

    private settingTypesAsArray = Object.keys(settingTypesBasicFields).map(key => settingTypesBasicFields[key]);

    buildCardField = (formFields, field) => {
        return this.buildFormField(formFields, field, true, false, undefined);
    }

    private availableBasicFieldDefinitionsIds = [
        settingTypesBasicFields.Title.id,
        settingTypesBasicFields.First_Name.id,
        settingTypesBasicFields.Last_Name.id,
        settingTypesBasicFields.Display_Name.id,
        settingTypesBasicFields.Second_Name.id,
        settingTypesBasicFields.Suffix.id,
        settingTypesBasicFields.Unique_Identifier.id,
        settingTypesBasicFields.Email_Address.id,
        settingTypesBasicFields.Pin.id,
        settingTypesBasicFields.Gender.id,
        settingTypesBasicFields.Print_Template.id,
        settingTypesBasicFields.Master_Type.id,
        settingTypesBasicFields.Current.id,
        settingTypesBasicFields.APB_Override.id,
        settingTypesBasicFields.Company.id,
        settingTypesBasicFields.Department.id]

    private availableCustomFieldDefinitionIds = [
        +fieldUiType.Text_Single.value,
        +fieldUiType.Text_Multi_Line.value,
        +fieldUiType.Text_Multi_Line_Adv.value,
        +fieldUiType.Select_Custom_Multi.value,
        +fieldUiType.Select_Multi.value,
        +fieldUiType.Select_Single.value,
        +fieldUiType.Select_Event.value,
        +fieldUiType.Select_Reader.value,
        +fieldUiType.Select_Zone.value,
        +fieldUiType.Select_Message.value,
        +fieldUiType.Select_Custom.value,
        +fieldUiType.Select_Email_Address.value,
        +fieldUiType.Select_Contact_Number.value,
        +fieldUiType.Select_Provided.value,
        +fieldUiType.Select_DTP.value,
        +fieldUiType.Select_Date.value,
        +fieldUiType.Select_Access_Group.value]

    /**
     * Used when constructing the summary page on wizards
     * @param formFields
     * @param field
     */
    buildSummaryField = (formFields, field) => {
        const accessor = `custom.${field.systemName}.name`;
        const newFormFields = (field.columnId && this.availableBasicFieldDefinitionsIds.includes(field.columnId))
            || (field.fieldTypeId && this.availableCustomFieldDefinitionIds.includes(+field.fieldTypeId))
            ? formFields.concat([[{
                secondColumnPreference: false,
                span: true,
                content: <SettingSummaryField accessor={accessor} displayName={field.displayName} field={field}></SettingSummaryField>
            }]])
            : formFields;
        return newFormFields
    }

    buildFormField = (formFields, field, isCard = false, isSummation = false, itemSiteId, isPresetFields?, hidePadding = false) => {
        const currentSettingTypeField = this.settingTypesAsArray.find(typeField => typeField.id === field.columnId);
        const fieldBundle = formFields.find(bundle => (isCard && bundle.length < 3) || (bundle.length === 1 && !bundle[0].span));
       
        if ((isCard && field.fieldTypeId) || (currentSettingTypeField && ((isCard && !currentSettingTypeField.cardData) || (!isCard && currentSettingTypeField.cardData)))) {
            return formFields;
        }

        const newFieldComponent = ((field) => {
            switch (field.columnId) {
                case settingTypesBasicFields.First_Name.id:
                case settingTypesBasicFields.Last_Name.id:
                case settingTypesBasicFields.Display_Name.id:
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <SettingTextField
                            key={`settingTextField-${field.columnId}`}
                            accessor={currentSettingTypeField?.accessor ?? ''}
                            required={!!currentSettingTypeField?.required}
                            displayName={field.displayName}
                            isCard={isCard}
                            autoFocus={field.displayName == 'First Name' || field.displayName == 'Make/Model' ? true : false}
                            validation={(value) => validateItemName(value, this.translate, ['#'])}
                            hidePadding={hidePadding}> </SettingTextField>
                    }
                case settingTypesBasicFields.Second_Name.id:
                case settingTypesBasicFields.Title.id:
                case settingTypesBasicFields.Suffix.id:
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <SettingTextField
                            key={`settingTextField-${field.columnId}`}
                            accessor={currentSettingTypeField?.accessor ?? ''}
                            required={!!currentSettingTypeField?.required}
                            displayName={field.displayName}
                            isCard={isCard}
                            hidePadding={hidePadding}> </SettingTextField>
                    }
                case settingTypesBasicFields.Unique_Identifier.id:
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <SettingTextField
                            key={`settingTextField-${field.columnId}`}
                            accessor={currentSettingTypeField?.accessor ?? ''}
                            required={!!currentSettingTypeField?.required}
                            displayName={field.displayName}
                            isCard={isCard}
                            validation={(value) => requiredValidation(value, this.translate)}
                            hidePadding={hidePadding}> </SettingTextField>
                    }
                case settingTypesBasicFields.Email_Address.id:
                case settingTypesBasicFields.Mobile_Number.id: // this is notifications, add two fields (email and mobile address)
                    {
                        return {
                            secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                            span: currentSettingTypeField?.span ?? false,
                            content: <SettingTextField
                                key={`settingTextField-${field.columnId}`}
                                accessor={currentSettingTypeField?.accessor ?? ''}
                                required={!!currentSettingTypeField?.required}
                                displayName={field.displayName}
                                isCard={isCard}
                                validation={(value) => settingTypesBasicFields.Email_Address.id == field.columnId ? validateEmail(value, this.translate, undefined) : isPhoneNumberValid(value, field.isRequired, this.translate)}
                                hidePadding={hidePadding}
                                handleWarning={settingTypesBasicFields.Mobile_Number.id == field.columnId ? (value) => isPhoneNumberPossibleValid(value, this.translate) : undefined}
                                onKeyDown={settingTypesBasicFields.Mobile_Number.id == field.columnId ? (event) => phoneNumberFieldRestriction(event) : undefined}
                            />
                        }
                    }
                case settingTypesBasicFields.Pin.id:
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <SettingTextField
                            key={`settingTextField-${field.columnId}`}
                            accessor={currentSettingTypeField?.accessor ?? ''}
                            required={!!currentSettingTypeField?.required}
                            displayName={field.displayName}
                            isCard={isCard}
                            maxLength={5}
                            onKeyDown={restrictToNumbersOnly}
                            isPassword={true}
                            hidePadding={hidePadding}> </SettingTextField>
                    }
                case settingTypesBasicFields.Gender.id:
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <SettingSelectField
                            key={`settingSelectField-${field.columnId}`}
                            accessor={currentSettingTypeField?.accessor ?? ''}
                            displayName={field.displayName}
                            required={!!currentSettingTypeField?.required}
                            selectType={SelectType.gender}
                            className="flex-1 pr-6"
                            itemSiteId={itemSiteId}> </SettingSelectField>
                    }
                case settingTypesBasicFields.Print_Template.id:
                    return {
                        // Print Template is not supported on Primo yet

                        // secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        // span: currentSettingTypeField?.span ?? false,
                        // content: <div id={`field-`+field.columnId} className="flex flex-col flex-1 pr-6">
                        //     <Label htmlFor={currentSettingTypeField?.accessor}
                        //         className="text-xs text-gray-600 mt-1">{field.displayName}:</Label>
                        //     <PaginatedSearchableSelectField
                        //         name={field.displayName}
                        //         key={`settingSelectField-${field.columnId}`}
                        //         accessor={currentSettingTypeField?.accessor ?? ''}
                        //         dataMethod={getPrintTemplatesWithPagination}
                        //         searchAccessor={'cardLebelTemplate'}
                        //         onSelect={(value) => this.formik.setFieldValue(currentSettingTypeField?.accessor, value)} />
                        // </div>
                    }
                case settingTypesBasicFields.Master_Type.id:
                case settingTypesBasicFields.Current.id:
                case settingTypesBasicFields.APB_Override.id: {
                    const height: string | undefined = (field.systemName === "DEFFIELDS_APB_OVERRIDE" || field.systemName === "DEFFIELDS_CURRENT") ? "59px" : undefined;
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <SettingToggleField
                            key={`settingToggleField-${field.columnId}`}
                            accessor={currentSettingTypeField?.accessor ?? ''}
                            displayName={field.displayName}
                            isCard={isCard}
                            height={height}
                        />
                    }
                }
                case settingTypesBasicFields.Company.id:
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <div className={`flex flex-col flex-1 mb-4 ${hidePadding ? "" : "pr-6"}`} id="company">
                            <Label htmlFor='companyId'
                                className="text-xs text-gray-600 mt-1">{this.translate.byKey(TranslationKeys.company)}:</Label>
                            <PaginatedSearchableSelectField
                                name={this.translate.byKey(TranslationKeys.company)}
                                key={`settingSelectField-${field.columnId}`}
                                accessor={currentSettingTypeField?.accessor ?? ''}
                                dataMethod={(listParameters?: ListQuery) => getCompanyNamesWithPagination({ ...listParameters, filters: appendFilter(itemSiteId, 'Site', 'site.id', listParameters?.filters ?? []) })}
                                searchAccessor={'compName'}
                                onSelect={(event, item) => {
                                    this.formik.setFieldValue('companyName', item?.label);
                                    this.formik.setFieldValue(currentSettingTypeField?.accessor, item?.value);
                                }}
                                onDelete={(event) => this.formik.setFieldValue(currentSettingTypeField?.accessor, undefined)}
                                dependent={{ accessor: "siteId", key: "site.id", name: "Site", value: itemSiteId, childAcceccor: currentSettingTypeField?.accessor ?? '' }}
                                selectedItemLabel={this.formik.values.companyName} />
                        </div>
                    }
                case settingTypesBasicFields.Department.id:
                    return {
                        secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                        span: currentSettingTypeField?.span ?? false,
                        content: <div className={`flex flex-col flex-1 ${hidePadding ? "" : "pr-6"}`} id="department">
                            <Label htmlFor='departmentId'
                                className="text-xs text-gray-600 mt-1">{this.translate.byKey(TranslationKeys.department)}:</Label>
                            <PaginatedSearchableSelectField
                                name={this.translate.byKey(TranslationKeys.department)}
                                key={`settingSelectField-${field.columnId}`}
                                accessor={currentSettingTypeField?.accessor ?? ''}
                                dataMethod={(listParameters?: ListQuery) => getDepartmentNamesWithPagination({ ...listParameters, filters: appendFilter(itemSiteId, 'Site', 'site.id', listParameters?.filters ?? []) })}
                                searchAccessor={'deptName'}
                                onSelect={(event, item) => {
                                    this.formik.setFieldValue('deptName', item?.label)
                                    this.formik.setFieldValue(currentSettingTypeField?.accessor, item?.value)
                                }}
                                onDelete={(event) => this.formik.setFieldValue(currentSettingTypeField?.accessor, undefined)}
                                dependent={{ accessor: "siteId", key: "site.id", name: "Site", value: itemSiteId, childAcceccor: currentSettingTypeField?.accessor ?? '' }}
                                selectedItemLabel={this.formik.values.deptName} />
                        </div>
                    }
                default:
                    switch (+field.fieldTypeId) {
                        case (+fieldUiType.Text_Single.value):
                            if (isPresetFields === true) {
                                return {
                                    secondColumnPreference: false,
                                    span: false,
                                    content: <SettingTextField
                                        key={`customSettingTextField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        displayName={field.displayName}
                                        isCard={false}
                                        isDecimal={field.id === '495' ? false : true}
                                        validation={(value) => {
                                            let error = field.isRequired ? validateZeroAllowed(value, field.min, field.id, this.translate) : undefined;
                                            let validateBoundsError = (field.min === 0 && field.max === 0) ? null : validateIntegerWithinBounds(value, field.min, field.max, this.translate);
                                            return validateBoundsError ? validateBoundsError : error;
                                        }}
                                        detailTypeId={field.masterDetailTypeId}
                                        numberUnit={field.unit}
                                        hidePadding={hidePadding}> </SettingTextField>
                                }
                            }
                            else {
                                const HOME_EMAIL_FIELD_ID = '1552';
                                const WORK_EMAIL_FIELD_ID = '1546';
                                return {
                                    secondColumnPreference: false,
                                    span: false,
                                    content: <SettingTextField
                                        key={`customSettingTextField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        displayName={field.displayName}
                                        isCard={false}
                                        detailTypeId={field.masterDetailTypeId}
                                        numberUnit={field.unit}
                                        validation={
                                            (value) => {
                                                if (field.id === HOME_EMAIL_FIELD_ID || field.id === WORK_EMAIL_FIELD_ID) {
                                                    return validateEmail(value, this.translate, undefined)
                                                }

                                                if (field.isRequired) {
                                                    return requiredValidation(value, this.translate);
                                                }

                                                return undefined;
                                            }
                                        }
                                        hidePadding={hidePadding}
                                    > </SettingTextField>
                                }
                            }
                        case (+fieldUiType.Text_Multi_Line.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingTextField
                                    key={`customSettingMultilineTextField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    isCard={false}
                                    multiLine={true}
                                    validation={field.isRequired ? (value) => requiredValidation(value, this.translate) : undefined}
                                    detailTypeId={field.masterDetailTypeId}
                                    hidePadding={hidePadding}> </SettingTextField>
                            }
                        case (+fieldUiType.Text_Multi_Line_Adv.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingTextField
                                    key={`customSettingMultilineCharacterCountTextField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    isCard={false}
                                    multiLine={true}
                                    characterCount={true}
                                    validation={field.isRequired ? (value) => requiredValidation(value, this.translate) : undefined}
                                    detailTypeId={field.masterDetailTypeId}
                                    hidePadding={hidePadding}> </SettingTextField>
                            }
                        case (+fieldUiType.Select_Custom_Multi.value):
                        case (+fieldUiType.Select_Multi.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingCheckboxListField
                                    key={`customSettingMultiSelectField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    isCard={false}
                                    options={field.options}
                                    detailTypeId={field.masterDetailTypeId}
                                    isSummation={isSummation}> </SettingCheckboxListField>
                            }
                        case (+fieldUiType.Select_Single.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingSelectField
                                    key={`customSettingToggleField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    options={field.options.sort((a, b) => a.ordinal - b.ordinal).map((option => { return { value: `${option.id}`, label: option.name } }))}
                                    detailTypeId={field.masterDetailTypeId}
                                    className="flex-1 pr-6"
                                    itemSiteId={itemSiteId}> </SettingSelectField>
                            }
                        case (+fieldUiType.Select_Event.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <div className="flex flex-col flex-1 pr-6">
                                    <Label htmlFor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        className="text-xs text-gray-600 mt-1">{field.displayName}:</Label>
                                    <PaginatedSearchableSelectField
                                        name={field.displayName}
                                        key={`customSettingSelectField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        dataMethod={getPrintTemplatesWithPagination}
                                        searchAccessor={'cardLabelTemplate'}
                                        required={true}
                                        onSelect={(event, item?) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.name`, item.label);
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, item.value);
                                        }}
                                        selectedItemLabel={this.formik.values.custom[field.systemName].name} />
                                </div>
                            }
                        case (+fieldUiType.Select_Reader.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <div className="flex flex-col flex-1 pr-6">
                                    <Label htmlFor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        className="text-xs text-gray-600 mt-1">{field.displayName}:</Label>
                                    <PaginatedSearchableSelectField
                                        name={field.displayName}
                                        key={`customSettingSelectField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        dataMethod={getReaderNamesWithPagination}
                                        searchAccessor={'devName'}
                                        required={true}
                                        onSelect={(event, item?) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.name`, item.label);
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, item.value);
                                        }}
                                        selectedItemLabel={this.formik.values.custom[field.systemName].name} />
                                </div>
                            }
                        case (+fieldUiType.Select_Zone.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <div className="flex flex-col flex-1 pr-6">
                                    <Label htmlFor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        className="text-xs text-gray-600 mt-1">{field.displayName}:</Label>
                                    <PaginatedSearchableSelectField
                                        name={field.displayName}
                                        key={`customSettingSelectField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        dataMethod={getZoneNamesWithPagination}
                                        searchAccessor={'zoneName'}
                                        required={true}
                                        onSelect={(event, item?) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.name`, item.label);
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, item.value);
                                        }}
                                        selectedItemLabel={this.formik.values.custom[field.systemName].name} />
                                </div>
                            }
                        case (+fieldUiType.Select_Message.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <div className="flex flex-col flex-1 pr-6">
                                    <Label htmlFor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        className="text-xs text-gray-600 mt-1">{field.displayName}:</Label>
                                    <PaginatedSearchableSelectField
                                        name={field.displayName}
                                        key={`customSettingSelectField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        dataMethod={getMessageNamesWithPagination}
                                        searchAccessor={'lmMessage'}
                                        required={true}
                                        onSelect={(event, item?) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.name`, item.label);
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, item.value);
                                        }}
                                        selectedItemLabel={this.formik.values.custom[field.systemName].name} />
                                </div>
                            }
                        case (+fieldUiType.Select_Custom.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingSelectField
                                    key={`customSettingSelectField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    options={field.options.sort((a, b) => a.ordinal - b.ordinal).map((option => { return { value: `${option.id}`, label: option.name } }))}
                                    detailTypeId={field.masterDetailTypeId}
                                    className="flex-1 pr-6"
                                    itemSiteId={itemSiteId}> </SettingSelectField>
                            }
                        case (+fieldUiType.Select_Email_Address.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingTextField
                                    key={`customSettingTextField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    detailTypeId={field.masterDetailTypeId}
                                    hidePadding={hidePadding}> </SettingTextField>
                            }
                        case (+fieldUiType.Select_Contact_Number.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingTextField
                                    key={`customSettingTextField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    tooltip={this.translate.byKey(TranslationKeys.phoneNumberFieldTooltip)}
                                    handleWarning={(value) => isPhoneNumberPossibleValid(value, this.translate)}
                                    validation={(value) => isPhoneNumberValid(value, field.isRequired, this.translate)}
                                    detailTypeId={field.masterDetailTypeId}
                                    onKeyDown={phoneNumberFieldRestriction}
                                    hidePadding={hidePadding} />
                            }
                        case (+fieldUiType.Select_Provided.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingSelectField
                                    key={`customSettingSelectField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    selectType={SelectType.event}
                                    options={[{ value: '1', label: 'Provided 1' }]}
                                    detailTypeId={field.masterDetailTypeId}
                                    className="flex-1 pr-6"
                                    itemSiteId={itemSiteId}> </SettingSelectField>
                            }
                        case (+fieldUiType.Select_DTP.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <div className="flex flex-col flex-1 pr-6">
                                    <Label htmlFor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        className="text-xs text-gray-600 mt-1">{field.displayName}:</Label>
                                    <PaginatedSearchableSelectField
                                        name={field.displayName}
                                        key={`customSettingSelectField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        dataMethod={getDeviceTimePatternNames}
                                        searchAccessor={'tpName'}
                                        onSelect={(event, item?) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.name`, item.label);
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, item.value);
                                        }}
                                        selectedItemLabel={this.formik.values.custom[field.systemName].name}
                                        pageSize={5} />
                                </div>
                            }
                        case (+fieldUiType.Select_Access_Group.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <div className="flex flex-col flex-1 pr-6">
                                    <Label htmlFor={`custom.${field.systemName}.value`}
                                        required={field.isRequired}
                                        className="text-xs text-gray-600 mt-1">{field.displayName}:</Label>
                                    <PaginatedSearchableSelectField
                                        name={field.displayName}
                                        key={`customSettingSelectField-${field.id}`}
                                        accessor={`custom.${field.systemName}.value`}
                                        dataMethod={(listQuery?: ListQuery) => getAccessGroupsNamesWithPagination(null, listQuery)}
                                        searchAccessor={'agName'}
                                        required
                                        onSelect={(event, item?) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.name`, item.label);
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, item.value);
                                        }}
                                        pageSize={5}
                                        selectedItemLabel={this.formik.values.custom[field.systemName].name} />
                                </div>
                            }
                        case (+fieldUiType.Select_Date.value):
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <SettingDateField
                                    key={`customSettingSelectField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    required={field.isRequired}
                                    displayName={field.displayName}
                                    detailTypeId={field.masterDetailTypeId}> </SettingDateField>
                            }
                        case (+fieldUiType.Toggle_Field.value):
                            return {
                                secondColumnPreference: currentSettingTypeField?.secondColumnPreference ?? false,
                                span: currentSettingTypeField?.span ?? false,
                                content: <SettingToggleField
                                    key={`settingToggleField-${field.id}`}
                                    accessor={`custom.${field.systemName}.value`}
                                    displayName={field.displayName}
                                    isCard={isCard} > </SettingToggleField>
                            }
                        case (+fieldUiType.Select_Multiple_DTPs.value):
                            const deviceTimePatternColumns = removeColumns(DeviceTimePatternColumns, ['startDate', 'endDate', 'site']);
                            const currentSelectedDTPs = this.formik.values.custom?.[field.systemName]?.value || [];
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <TableContextProvider
                                    dataMethod={(listParameters?: ListQuery) => getDeviceTimePatterns(this.translate, listParameters)}
                                    tableDescriptor={deviceTimePatternColumns}
                                    searchDescriptor={DeviceTimePatternSearch}
                                    useFilter
                                    useSort
                                    usePagination
                                    useSearch
                                    useTable
                                    customDelete={{ fieldName: "isDefault", comparisonValue: false }}>
                                    <TableTab
                                        title={field.displayName}
                                        selectRows={true}
                                        selectedIds={currentSelectedDTPs}
                                        setSelectedIds={(selectedIds: string[]) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, selectedIds);
                                        }}
                                        setSelectedRows={(deviceTimePatterns: DeviceTimePattern[]) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.names`, deviceTimePatterns.map(deviceTimePattern => deviceTimePattern.name));
                                        }}
                                        cyName="tbl_deviceTimePatterns" />
                                </TableContextProvider >
                            }
                        case (+fieldUiType.Select_Multiple_Readers.value):
                            const currentSelectedReaders = this.formik.values.custom?.[field.systemName]?.value || [];
                            return {
                                secondColumnPreference: false,
                                span: false,
                                content: <TableContextProvider
                                    dataMethod={(listParameters?: ListQuery) => getDeviceImprox({
                                        ...listParameters,
                                        filters: appendFilter(`${DEVICE_CATEGORIES.READER}`, "Category", "unitType.utCategory", listParameters?.filters ?? [])
                                    })}
                                    tableDescriptor={DeviceImproxColumns}
                                    searchDescriptor={DeviceImproxSearch}
                                    useFilter
                                    useSort
                                    usePagination
                                    useSearch
                                    useTable
                                    customDelete={{ fieldName: "isDefault", comparisonValue: false }}>
                                    <TableTab
                                        title={field.displayName}
                                        selectRows={true}
                                        selectedIds={currentSelectedReaders}
                                        setSelectedIds={(selectedIds: string[]) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.value`, selectedIds);
                                        }}
                                        setSelectedRows={(readers: DeviceImprox[]) => {
                                            this.formik.setFieldValue(`custom.${field.systemName}.names`, readers.map(reader => reader.name));
                                        }}
                                        cyName="tbl_readers" />
                                </TableContextProvider >
                            }
                    }
                    return undefined;
            }
        })(field);

        if (!newFieldComponent) {
            return formFields;
        }
        else if (fieldBundle && !currentSettingTypeField?.span) {
            if (fieldBundle[0].secondColumnPreference) {
                fieldBundle.splice(0, 0, newFieldComponent);
            }
            else {
                fieldBundle.push(newFieldComponent)
            }
            return formFields;
        }
        else {
            return formFields.concat([[newFieldComponent]]);
        }
    }
}
